import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import * as Highcharts from 'highcharts';

import { MatAccordion } from '@angular/material/expansion';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';

import { DashboardService } from './dashboard.service';
import mockdata from './mockdata.json';
import { stripeDataGridColumns } from './models/stripe-data.model';
import { PaginationService } from '../../helpers/pagination.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild(MatAccordion) accordion!: MatAccordion;

  visibleFieldKeys: string[] = [
    'select',
    'Id',
    'Stripe_Id',
    'Payment_Type',
    'Payment_Source',
    'Created_on',
    'Available_on',
    'Amount',
    'TaskId',
    'Transfer_group',
    'Payment_Dpt',
    'StatusText',
    'ShopperEmail',
    'ImportCounter',
  ];

  data = mockdata;
  selection = new SelectionModel<any>(true, []);
  pageLimit = 1000;
  currentPageSize: number = 10;
  currentPageIndex: number = 0;
  columns = stripeDataGridColumns;
  displayedColumns: string[] = [];
  // this.columns
  // .filter(col => !!col.key)
  // .map(col => col.key);
  dataSource = new MatTableDataSource<any>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  sortedData: any[] = [];

  constructor(
    private _dashboardService: DashboardService,
    private paginationService: PaginationService,
  ) {}

  ngOnInit(): void {
    this.dataSource.data = [...this.dataSource.data, ...this.data];
    this.sortedData = this.dataSource.data.slice(0, this.currentPageSize);
    this.barChart();
    this.lineChart();
    this.pieCharts();
    this.columnCharts();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    const startIndex = this.currentPageIndex * this.currentPageSize;
    const endIndex = startIndex + this.currentPageSize;
    // this.paginator.length = this.dataSource.data.length;

    this.sortedData = this.dataSource.data.slice(startIndex, endIndex);
    // this.sortCurrentPageData(); // Apply sorting after fetching data
    this.updateDisplayedColumns();

    // Ensure sorting works by listening to sort changes
    this.sort.sortChange.subscribe(() => {
      this.sortCurrentPageData();
    });
  }

  updateDisplayedColumns() {
    this.displayedColumns = this.visibleFieldKeys;
  }

  toggleColumnVisibility(key: string) {
    if (this.visibleFieldKeys.includes(key)) {
      this.visibleFieldKeys = this.visibleFieldKeys.filter((k) => k !== key);
    } else {
      this.visibleFieldKeys.push(key);
    }
    this.updateDisplayedColumns();
  }

  sortCurrentPageData() {
    if (this.sort.active && this.sort.direction !== '') {
      const startIndex = this.currentPageIndex * this.currentPageSize;
      const endIndex = startIndex + this.currentPageSize;
      this.paginator.length = this.dataSource.data.length;

      // Slice data for current page and apply sorting
      const currentPageData = this.dataSource.data.slice(startIndex, endIndex);
      this.sortedData = currentPageData.sort((a, b) => {
        const isAsc = this.sort.direction === 'asc';
        const isDesc = this.sort.direction === 'desc';
        switch (this.sort.active) {
          case 'Id':
            return this.compare(a.Id, b.Id, isAsc);
          case 'Stripe_Id':
            return this.compare(a.Stripe_Id, b.Stripe_Id, isAsc);
          case 'Payment_Type':
            return this.compare(a.Payment_Type, b.Payment_Type, isAsc);
          case 'Payment_Source':
            return this.compare(a.Payment_Source, b.Payment_Source, isAsc);
          case 'Created_on':
            return this.compare(a.Created_on, b.Created_on, isAsc);
          case 'Available_on':
            return this.compare(a.Available_on, b.Available_on, isAsc);
          case 'Amount':
            return this.compare(a.Amount, b.Amount, isAsc);
          case 'Created_date':
          case 'Modified_date':
          case 'Transfer_Date':
            return this.sortingDate(
              this.parseDate(a.executionDate),
              this.parseDate(b.executionDate),
              isDesc,
            );
          default:
            return 0;
        }
      });
    }
  }

  // Custom comparison function
  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sortingDate(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean,
  ) {
    // Convert string dates to Date objects if they are not already
    const dateA = new Date(a);
    const dateB = new Date(b);

    // Compare the dates
    return (dateA.getTime() - dateB.getTime()) * (isAsc ? 1 : -1);
  }

  parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number); // Split the string and convert to numbers
    return new Date(year, month - 1, day); // Create a Date object (month is zero-based)
  }

  onPageChange($event: PageEvent) {
    this.currentPageIndex = $event.pageIndex;
    this.currentPageSize = $event.pageSize;
    // Save the pagination state before navigating
    this.paginationService.savePaginationState(
      this.currentPageIndex,
      this.currentPageSize,
      this.dataSource.data.length,
    );
    const startIndex = this.currentPageIndex * this.currentPageSize;
    let endIndex = startIndex + this.currentPageSize;
    // let endIndexVal = endIndex;
    if (endIndex > this.dataSource.data.length) {
      endIndex = this.dataSource.data.length;
    }
    this.paginator._intl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      return `${startIndex + 1} - ${endIndex} of ${this.dataSource.data.length}`;
    };

    this.sortedData = this.dataSource.data.slice(startIndex, endIndex);
    this.sortCurrentPageData(); // Apply sorting after fetching data

    // if (($event.pageIndex + 1) * $event.pageSize == this.resultsLen - $event.pageSize) {
    //   this.fetchPaginatedData($event.pageSize, this.selectedCategoryIds, this.selectedStoreIds, this.selectedStatusIds);
    // } else {
    //   // Attach paginator and sorting after data is updated
    //   this.dataSource.paginator = this.paginator;
    // }
    // Check if the user has reached near the end and needs to load more data
    const offsetReached = ($event.pageIndex + 1) * $event.pageSize;
    const isNearEnd = offsetReached >= this.dataSource.data.length;

    this.barChartOptions = {
      ...this.barChartOptions,
      xAxis: {
        categories: this.sortedData.map((item) => item.Created_on),
      },
      series: [
        {
          name: 'Amount',
          type: 'bar',
          data: this.sortedData.map((item) => item.Amount),
        },
      ],
    };
  }

  Highcharts: typeof Highcharts = Highcharts;
  barChartOptions: Highcharts.Options = {};
  lineChartOptions: Highcharts.Options = {};
  pieChartOptions: Highcharts.Options = {};
  columnChartOptions: Highcharts.Options = {};

  barChart() {
    this.barChartOptions = {
      chart: {
        type: 'bar',
      },
      title: {
        text: 'Stripe Data Chart',
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
          },
        },
      },
      xAxis: {
        title: {
          text: 'Date',
        },
        categories: this.sortedData.map((item) => item.Created_on),
      },
      yAxis: {
        title: {
          text: 'Amount',
        },
      },
      series: [
        {
          name: 'Amount',
          type: 'bar',
          data: this.sortedData.map((item) => item.Amount),
        },
      ],
    };
  }

  lineChart() {
    this.lineChartOptions = {
      chart: {
        type: 'line',
      },
      title: {
        text: 'Stripe Data Chart',
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
          },
          showInLegend: true,
        },
      },
      xAxis: {
        title: {
          text: 'Date',
        },
        categories: this.sortedData.map((item) => item.Created_on),
      },
      yAxis: {
        title: {
          text: 'Amount',
        },
      },
      series: [
        {
          name: 'Amount',
          type: 'line',
          data: this.sortedData.map((item) => item.Amount),
        },
      ],
    };
  }

  pieCharts() {
    this.pieChartOptions = {
      chart: {
        type: 'pie',
      },
      title: {
        text: 'Stripe Data Chart',
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',

          dataLabels: {
            enabled: false,
          },

          showInLegend: true,
        },
      },
      xAxis: {
        title: {
          text: 'Date',
        },
        categories: this.sortedData.map((item) => item.Created_on),
      },
      yAxis: {
        title: {
          text: 'Amount',
        },
      },
      series: [
        {
          name: 'Amount',
          type: 'pie',
          data: this.sortedData.map((item) => item.Amount),
        },
      ],
    };
  }

  columnCharts() {
    this.columnChartOptions = {
      chart: {
        type: 'column',
      },
      title: {
        text: 'Stripe Data Chart',
      },

      xAxis: {
        title: {
          text: 'Date',
        },
        categories: this.sortedData.map((item) => item.Created_on),
      },
      yAxis: {
        title: {
          text: 'Amount',
        },
      },
      series: [
        {
          name: 'Amount',
          type: 'column',
          data: this.sortedData.map((item) => item.Amount),
        },
      ],
    };
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }
}
